import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";

const OVERLAY_Z = 99999;
const TOOLTIP_Z = OVERLAY_Z + 1;
const PADDING = 8;
const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  zIndex: OVERLAY_Z,
  pointerEvents: "auto",
};

const spotlightStyle = (rect) => ({
  position: "fixed",
  top: rect.top - PADDING,
  left: rect.left - PADDING,
  width: rect.width + PADDING * 2,
  height: rect.height + PADDING * 2,
  borderRadius: 10,
  boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.65)",
  zIndex: OVERLAY_Z,
  pointerEvents: "none",
  transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
});

const tooltipBaseStyle = {
  position: "fixed",
  zIndex: TOOLTIP_Z,
  background: "#fff",
  borderRadius: 14,
  padding: "20px 24px",
  maxWidth: 380,
  minWidth: 280,
  boxShadow: "0 20px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.05)",
  transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
};

const btnBase = {
  border: "none",
  borderRadius: 8,
  padding: "8px 18px",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  transition: "all 0.15s ease",
};

const getTooltipPosition = (rect, tooltipRef) => {
  const gap = 14;
  const tooltip = tooltipRef.current;
  const tw = tooltip ? tooltip.offsetWidth : 340;
  const th = tooltip ? tooltip.offsetHeight : 200;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const spotBottom = rect.top + rect.height + PADDING;
  const spotTop = rect.top - PADDING;
  const spotCenterX = rect.left + rect.width / 2;

  let top, left, placement;

  if (spotBottom + gap + th < vh) {
    placement = "bottom";
    top = spotBottom + gap;
    left = Math.max(16, Math.min(spotCenterX - tw / 2, vw - tw - 16));
  } else if (spotTop - gap - th > 0) {
    placement = "top";
    top = spotTop - gap - th;
    left = Math.max(16, Math.min(spotCenterX - tw / 2, vw - tw - 16));
  } else {
    placement = "bottom";
    top = Math.max(16, Math.min(spotBottom + gap, vh - th - 16));
    left = Math.max(16, Math.min(spotCenterX - tw / 2, vw - tw - 16));
  }

  return { top, left, placement };
};

const GuidedTour = ({ steps, isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef(null);

  const step = steps[currentStep];

  const measureRect = useCallback(() => {
    if (!step?.target) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(step.target);
    if (el) {
      const r = el.getBoundingClientRect();
      setTargetRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      });
    } else {
      setTargetRect(null);
    }
  }, [step]);

  const rafMeasure = useCallback(() => {
    requestAnimationFrame(measureRect);
  }, [measureRect]);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!visible) return;
    if (!step?.target) {
      setTargetRect(null);
      return;
    }

    const el = document.querySelector(step.target);
    if (!el) {
      setTargetRect(null);
      return;
    }

    el.scrollIntoView({ behavior: "smooth", block: "nearest" });

    const t1 = setTimeout(rafMeasure, 50);
    const t2 = setTimeout(rafMeasure, 300);
    const t3 = setTimeout(rafMeasure, 600);

    window.addEventListener("resize", rafMeasure);
    document.addEventListener("scroll", rafMeasure, true);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener("resize", rafMeasure);
      document.removeEventListener("scroll", rafMeasure, true);
    };
  }, [visible, currentStep, step, rafMeasure]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      onClose();
    }
  }, [currentStep, steps.length, onClose]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  }, [currentStep]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") handleNext();
      if (e.key === "ArrowLeft") handlePrev();
    },
    [onClose, handleNext, handlePrev],
  );

  useEffect(() => {
    if (!visible) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [visible, handleKeyDown]);

  if (!visible || !step) return null;

  const isLast = currentStep === steps.length - 1;
  const isFirst = currentStep === 0;
  const hasTarget = !!targetRect;

  const tooltipPos = hasTarget
    ? getTooltipPosition(targetRect, tooltipRef)
    : {
        top: window.innerHeight / 2 - 120,
        left: window.innerWidth / 2 - 190,
        placement: "center",
      };

  return createPortal(
    <>
      {/* Clickable overlay to close */}
      <div style={overlayStyle} onClick={onClose} />

      {/* Spotlight hole */}
      {hasTarget && <div style={spotlightStyle(targetRect)} />}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        style={{
          ...tooltipBaseStyle,
          top: tooltipPos.top,
          left: tooltipPos.left,
        }}
      >
        {/* Step counter */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 10,
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "linear-gradient(135deg, #3b82f6, #2563eb)",
              color: "#fff",
              borderRadius: 20,
              padding: "3px 12px",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            <i className="fa fa-graduation-cap" style={{ fontSize: 10 }} />
            {currentStep + 1} / {steps.length}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#94a3b8",
              cursor: "pointer",
              fontSize: 18,
              lineHeight: 1,
              padding: 0,
            }}
            title="Fermer le guide"
          >
            ×
          </button>
        </div>

        {/* Title */}
        {step.title && (
          <div
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: "#1e293b",
              marginBottom: 6,
              lineHeight: 1.3,
            }}
          >
            {step.title}
          </div>
        )}

        {/* Content */}
        <div
          style={{
            fontSize: 13,
            color: "#475569",
            lineHeight: 1.6,
            marginBottom: 16,
          }}
        >
          {step.content}
        </div>

        {/* Navigation */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            {!isFirst && (
              <button
                onClick={handlePrev}
                style={{
                  ...btnBase,
                  background: "#f1f5f9",
                  color: "#475569",
                }}
              >
                <i className="fa fa-arrow-left mr-1" style={{ fontSize: 10 }} />
                Précédent
              </button>
            )}
          </div>
          <button
            onClick={handleNext}
            style={{
              ...btnBase,
              background: isLast
                ? "linear-gradient(135deg, #22c55e, #16a34a)"
                : "linear-gradient(135deg, #3b82f6, #2563eb)",
              color: "#fff",
              boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
            }}
          >
            {isLast ? (
              <>
                Terminer
                <i className="fa fa-check ml-2" style={{ fontSize: 10 }} />
              </>
            ) : (
              <>
                Suivant
                <i
                  className="fa fa-arrow-right ml-2"
                  style={{ fontSize: 10 }}
                />
              </>
            )}
          </button>
        </div>

        {/* Progress dots */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 5,
            marginTop: 14,
          }}
        >
          {steps.map((_, i) => (
            <div
              key={i}
              onClick={() => setCurrentStep(i)}
              style={{
                width: i === currentStep ? 18 : 7,
                height: 7,
                borderRadius: 4,
                background: i === currentStep ? "#3b82f6" : "#e2e8f0",
                cursor: "pointer",
                transition: "all 0.25s ease",
              }}
            />
          ))}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default GuidedTour;
